import React, { useEffect, useState } from 'react';
import { Collapse } from '@material-tailwind/react';
import TextWinds from '../common/TextWinds';
import CheckBoxes from '../common/CheckBoxes';
import { Terms } from '../../constants/types';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { openModal } from '../../store/features/modalSlice';
import { AgreementModal, AgreementType } from '../common/Agreement';
import Buttons from '../common/Buttons';
import { useAppDispatch } from '../../store/hook';

type PaymentTermRow = { id: string; label: AgreementType; isChecked: boolean };

interface TermsAgreementProps {
  /** 추가 약관 목록 */
  termsList: Terms[];
  onAllRequiredChecked: (allChecked: boolean) => void;
}

/**
 * 결제 페이지 - 약관 동의 컴포넌트
 * @description 모든 상품에 대한 기본 결제 약관 : 하드코딩해둠. 추후 어드민화 하면서 고도화 필요.
 * @description 기본 약관 외 추가 약관 : template에서 제공하는 약관 목록을 사용.
 * @param termsList 약관 목록
 * @param onAllRequiredChecked 모든 약관 동의 여부 변경 핸들러 (결제 가능 여부 판단)
 */
const TermsAgreement = ({
  termsList,
  onAllRequiredChecked,
}: TermsAgreementProps) => {
  const dispatch = useAppDispatch();
  const [isTermsInfoOpen, setIsTermsInfoOpen] = useState(false);
  const [orderInfoAgreed, setOrderInfoAgreed] = useState(false);
  const [paymentTermAgree, setPaymentTermAgree] = useState<PaymentTermRow[]>([
    { id: 'agree_1', label: AgreementType.REFUND, isChecked: false },
  ]);

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  /** termsList 로딩 후 상태 초기화 (참조가 아닌 ID 기준으로 비교) */
  const termsKey = termsList?.map((t) => t.id).join(',') ?? '';
  useEffect(() => {
    setCheckedIds(new Set());
    setOrderInfoAgreed(false);
    setPaymentTermAgree([
      { id: 'agree_1', label: AgreementType.REFUND, isChecked: false },
    ]);
    onAllRequiredChecked(false);
  }, [termsKey]);

  /** 모든 약관 동의 여부 판단 핸들러 */
  const emitAllRequiredStatus = (
    ids: Set<string> = checkedIds,
    pay: PaymentTermRow[] = paymentTermAgree,
    order: boolean = orderInfoAgreed,
  ) => {
    const requiredTerms = (termsList ?? []).filter((t) => t.isRequired);
    const allApiRequired =
      requiredTerms.length === 0 || requiredTerms.every((t) => ids.has(t.id));
    const allPaymentTerms = pay.every((t) => t.isChecked);
    onAllRequiredChecked(order && allPaymentTerms && allApiRequired);
  };

  /** 추가 약관 동의 - 항목별 동의 체크 핸들러 */
  const handleCheck = (id: string, checked: boolean) => {
    const next = new Set(checkedIds);
    checked ? next.add(id) : next.delete(id);
    setCheckedIds(next);
    emitAllRequiredStatus(next);
  };

  /** 기본 결제 약관 동의 - 상단 전체 동의 체크 핸들러 (동그라미 체크박스) */
  const handleOrderInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setOrderInfoAgreed(checked);
    // 상단 체크박스가 하위 약관 항목의 전체 동의 역할 수행
    const nextPay = paymentTermAgree.map((item) => ({
      ...item,
      isChecked: checked,
    }));
    setPaymentTermAgree(nextPay);
    emitAllRequiredStatus(checkedIds, nextPay, checked);
  };

  /** 기본 결제 약관 동의 - 하위 항목별 동의 체크 핸들러 (네모 체크박스) */
  const handlePaymentTermCheck = (id: string, checked: boolean) => {
    const nextPay = paymentTermAgree.map((item) =>
      item.id === id ? { ...item, isChecked: checked } : item,
    );
    setPaymentTermAgree(nextPay);
    // 하위 항목 전체 체크 시 상단 체크박스 자동 체크, 하나라도 해제 시 상단도 해제
    const allSubChecked = nextPay.every((t) => t.isChecked);
    setOrderInfoAgreed(allSubChecked);
    emitAllRequiredStatus(checkedIds, nextPay, allSubChecked);
  };

  /** 카테고리별 그룹핑 처리 */
  const grouped = (termsList ?? []).reduce(
    (acc, term) => {
      if (!term.category?.id) return acc;
      const key = term.category.id;
      if (!acc[key]) acc[key] = { category: term.category, terms: [] };
      acc[key].terms.push(term);
      return acc;
    },
    {} as Record<
      string,
      { category: { id: string; name: string }; terms: Terms[] }
    >,
  );

  return (
    <>
      {/* 기본 결제 약관 동의 */}
      <div className="mt-4 flex flex-col gap-4 rounded-md bg-white p-6">
        <TextWinds type="title_h4">결제 약관 동의</TextWinds>
        <div className="flex flex-row items-center justify-between">
          <CheckBoxes
            type="rounded"
            color="purple5"
            labelValue={
              '(필수)위 주문의 상품 및 결제, 주문 정보 등을 확인하였으며,구매 진행에 동의합니다.'
            }
            checked={orderInfoAgreed}
            onChange={handleOrderInfoChange}
          />

          <div className="flex items-start justify-center">
            <ChevronDownIcon
              role="button"
              aria-label="결제 약관 동의 펼치기"
              strokeWidth={2.5}
              className={`h-5 w-5 transition-transform ${
                isTermsInfoOpen ? 'rotate-180' : ''
              }`}
              onClick={() => setIsTermsInfoOpen((cur: boolean) => !cur)}
            />
          </div>
        </div>
        <div>
          <Collapse open={isTermsInfoOpen}>
            <div className="flex flex-col gap-[8px]">
              {paymentTermAgree.map((item) => (
                <div
                  className="flex flex-row items-center gap-[8px]"
                  key={item.id}
                >
                  <CheckBoxes
                    id={item.id}
                    labelValue={item.label}
                    type="full"
                    checked={item.isChecked}
                    onChange={(e) =>
                      handlePaymentTermCheck(item.id, e.target.checked)
                    }
                  />
                  <div>
                    <Buttons
                      type="text"
                      color="purple5"
                      onClick={() =>
                        dispatch(
                          openModal({
                            body: <AgreementModal agreement={item.label} />,
                          }),
                        )
                      }
                    >
                      자세히 보기
                    </Buttons>
                  </div>
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      </div>

      {/* 추가 약관 동의 */}
      {Object.values(grouped).map(({ category, terms }) => (
        <div
          key={category.id}
          className="mt-4 flex flex-col gap-4 rounded-md bg-white p-6"
        >
          <TextWinds type="title_h4">{category.name} 약관 동의</TextWinds>
          {terms.map((term) => (
            <CheckBoxes
              key={term.id}
              type="rounded"
              color="purple5"
              labelValue={`(${term.isRequired ? '필수' : '선택'}) ${term.content}`}
              checked={checkedIds.has(term.id)}
              onChange={(e) => handleCheck(term.id, e.target.checked)}
            />
          ))}
        </div>
      ))}
    </>
  );
};

export default TermsAgreement;
